import dayjs from "dayjs";
import { communicationUnit, SURVEY_COMMUNICATION_SUBTYPE_KEY, BOOKING_COMMUNICATION_SUBTYPE_KEY, } from "../constants/communications.js";
const tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
// Thanks to:
// http://fightingforalostcause.net/misc/2006/compare-email-regex.php
// http://thedailywtf.com/Articles/Validating_Email_Addresses.aspx
// http://stackoverflow.com/questions/201323/what-is-the-best-regular-expression-for-validating-email-addresses/201378#201378
// https://en.wikipedia.org/wiki/Email_address The format of an email address is local-part@domain, where the
// local part may be up to 64 octets long and the domain may have a maximum of 255 octets.[4]
export const isEmailValid = (email) => {
    if (!email) {
        return true;
    }
    const emailParts = email.split("@");
    if (emailParts.length !== 2) {
        return false;
    }
    const account = emailParts[0];
    const address = emailParts[1];
    if (account.length > 64) {
        return false;
    }
    if (address.length > 255) {
        return false;
    }
    const domainParts = address.split(".");
    if (domainParts.some((part) => part.length > 63)) {
        return false;
    }
    return tester.test(email);
};
export const getTextWithReplacements = (options) => {
    const { text = "", replacements = {}, replacementPlaceholders = [], handleSurveyLink = false, handleLogo = false, handlePdfUrl = false, replaceNewLines = false, isEmailBody = false, isSmsBody = false, } = options;
    if (!Object.keys(replacements).length)
        return text;
    let newText = text;
    // Special handling for email body
    if (isEmailBody) {
        if (handleSurveyLink) {
            newText = newText.replaceAll("{surveyLink}", replacements.surveyLink
                ? `<a href=${replacements.surveyLink} target="_blank">&lt;SURVEY_LINK&gt;</a>`
                : "");
        }
        if (handleLogo) {
            newText = newText.replaceAll("{logo}", replacements.logo ? `<img src="${replacements.logo}" />` : "");
        }
        if (handlePdfUrl) {
            newText = newText.replaceAll("{pdfUrl}", replacements.pdfUrl
                ? `<a href=${replacements.pdfUrl} target="_blank">&lt;PDF_LINK&gt;</a>`
                : "");
        }
    }
    // Special handling for SMS body
    if (isSmsBody && replaceNewLines) {
        newText = newText.replaceAll("\n", "<br>");
    }
    // Generic replacements
    replacementPlaceholders.forEach((key) => {
        const value = replacements[key];
        newText = newText.replaceAll(`{${key}}`, value || "");
    });
    return newText;
};
export const getCommunicationDefaultReplacements = ({ logo, }) => {
    return {
        firstName: "John",
        lastName: "Doe",
        appointmentDate: dayjs().format("DD/MM/YYYY"),
        appointmentStartTime: dayjs().format("hh:mm a"),
        appointmentEndTime: dayjs().add(30, "minute").format("hh:mm a"),
        providerFirstName: "Best",
        providerLastName: "Provider",
        serviceName: "Routine Service",
        serviceInstructions: "Service Instructions: Here are some instructions to follow...",
        locationName: "Head Office",
        locationAddress: "425 Smith Street, Melbourne, 9999, Tasmania, Australia",
        locationNotes: "Location Notes: Here are some notes to help you find this location...",
        surveyLink: "https://atlasone.co/",
        bookingId: "100",
        logo,
        pdfUrl: "https://atlasone.co/",
        downloadLink: "https://atlasone.co/download",
        affiliateNotes: "<strong>Affiliate Notes:</strong> Here are some notes from the affiliate...",
        companyName: "AtlasOne",
    };
};
/**
 * Formats a human-readable schedule description for a communication template.
 *
 * @param {CommunicationTemplate} row - The communication template object containing scheduling information.
 * @returns {string} A string describing when the communication will be sent (e.g., "Sends 5 minutes before the event" or "Sends immediately").
 */
export const formatCommunicationSchedule = (row) => {
    const { sendTimeOffset, sendTimeUnit } = row;
    if (sendTimeOffset === 0 ||
        sendTimeOffset === null ||
        sendTimeUnit === null) {
        return "Sends immediately";
    }
    const unit = `${communicationUnit[sendTimeUnit].label.toLowerCase()}`;
    const direction = row.subtype !== SURVEY_COMMUNICATION_SUBTYPE_KEY.PRE_SERVICE &&
        row.subtype !== BOOKING_COMMUNICATION_SUBTYPE_KEY.REMINDER
        ? "after"
        : "before";
    return `Sends ${sendTimeOffset} ${unit} ${direction} the event`;
};
/**
 * Builds a replacements object for booking communication templates (email/SMS) using booking and related data.
 *
 * @param {Booking} booking - The booking object, including associations (Respondent, Location, Service, Manager).
 * @param {string} logoUrl - The URL for the logo image to be used in the template.
 * @param {string} pdfUrl - The URL for the PDF to be used in the template.
 * @param {{ id: string; name: string; url: string }} surveyLink - The survey link object or string to be used for the {surveyLink} placeholder.
 * @returns {object} An object containing all replacement variables for the template.
 */
export const bookingCommunicationReplacementBuilder = (booking, logoUrl, pdfUrl, surveyLink) => {
    const respondent = booking.Respondent;
    const location = booking.Location;
    const service = booking.Service;
    const manager = booking.Manager;
    const company = booking.Company;
    const getAddress = (loc) => {
        if (!loc)
            return "-";
        const addressFields = ["address", "city", "postalCode", "state", "country"];
        return addressFields
            .map((f) => loc[f])
            .filter(Boolean)
            .join(", ");
    };
    const getPreferredTimeFrameLabel = (preferredTimeFrame) => {
        if (preferredTimeFrame === "am")
            return "within AM";
        if (preferredTimeFrame === "pm")
            return "within PM";
        if (preferredTimeFrame === "anytime")
            return "Anytime";
        return preferredTimeFrame;
    };
    // Helper for appointment time
    const getAppointmentTime = (mode) => {
        if (!booking.respondentAvailabilityIsKnown)
            return "TBD";
        if (mode === "start" &&
            typeof booking.startHours === "number" &&
            typeof booking.startMinutes === "number") {
            return dayjs()
                .hour(booking.startHours)
                .minute(booking.startMinutes)
                .format("hh:mm a");
        }
        if (mode === "end" &&
            typeof booking.endHours === "number" &&
            typeof booking.endMinutes === "number") {
            return dayjs()
                .hour(booking.endHours)
                .minute(booking.endMinutes)
                .format("hh:mm a");
        }
        if (booking.preferredTimeFrame) {
            return getPreferredTimeFrameLabel(booking.preferredTimeFrame);
        }
        return "TBD";
    };
    return {
        bookingId: booking.id?.toString(),
        firstName: respondent?.firstName || "",
        lastName: respondent?.lastName || "",
        appointmentDate: booking.respondentAvailabilityIsKnown && booking.date
            ? dayjs(booking.date).format("DD/MM/YYYY")
            : "TBD",
        appointmentStartTime: getAppointmentTime("start"),
        appointmentEndTime: getAppointmentTime("end"),
        providerFirstName: manager?.firstName || "",
        providerLastName: manager?.lastName || "",
        serviceName: service?.name || "",
        serviceInstructions: service?.instructions || "",
        locationName: booking.isMobileBooking
            ? booking.mobileBookingClientAddress || ""
            : location?.name || booking.affiliateLocation || "-",
        locationAddress: booking.isMobileBooking ? null : getAddress(location),
        locationNotes: location?.notes || "",
        logo: logoUrl || "",
        pdfUrl: pdfUrl || "",
        affiliateNotes: booking.affiliateSelected
            ? (booking.affiliateNotes ?? "")
            : "",
        surveyLink: surveyLink || "",
        companyName: company?.name || "",
    };
};
//# sourceMappingURL=communication.js.map